import Service from '@/pages/dashboard/components/PageContainer/Service';
import dashboardRequest from '@/pages/dashboard/utils/dashboardRequest';
import { stringToFunction } from '@/pages/dashboard/utils/stringToJson';
import { getDefaultUtcDateValue } from '@/pages/dashboard/utils/dateUtils';
import { formatSingleTime } from '@/pages/dashboard/utils/utils';
import React, { useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'umi';
import { expandKpiTrendRangeToMinDays } from '../KpiEventMetricTrendService/_utils';
import type { TKpiOverviewDataScope } from '../../utils/uaRoiUaMediaScopeFilters';

/** 单维度下的异常说明 */
export interface IAnomalyDimItem {
  anomaly_explain: string;
  date: string;
}

/** 单点（旧接口）或按日列表（新接口） */
export type TAnomalyDimSeries = IAnomalyDimItem | IAnomalyDimItem[];

/**
 * 指标 key → 维度 key → 异常说明（单点或按日多条）
 * 例如 spend.All、applovin_d7.1 → [{ date, anomaly_explain }, ...]
 */
export type TAnomalyResultMap = Record<string, Record<string, TAnomalyDimSeries>>;

/**
 * 新接口 anomaly_result：`data` 下分 `all` / `uaroi` 两套指标树；旧接口扁平结构规范为仅填 `all`。
 */
export type TKpiOverviewAnomalyBundle = {
  all: TAnomalyResultMap;
  uaroi?: TAnomalyResultMap;
};

function isAnomalyDimLeaf(v: unknown): v is IAnomalyDimItem {
  if (!v || typeof v !== 'object') {
    return false;
  }
  const o = v as {
    date?: unknown;
    anomaly_explain?: unknown;
  };
  return typeof o.date === 'string' && typeof o.anomaly_explain === 'string';
}

/**
 * 从各类入参中取出「指标 → 维度 → 异动」根对象。
 * - axios 成功拦截器已解包：入参多为业务 data，即 { spend: {...}, d7: {...} }
 * - 本地 / 函数 mock 可能仍传整包：{ code, msg, data: { ... } }
 * - 旧壳：{ data: { applovin_d7: ... } } 仅一层 data 包裹指标表
 */
function extractAnomalyResultRoot(res: unknown): Record<string, unknown> | null {
  if (!res || typeof res !== 'object' || Array.isArray(res)) {
    return null;
  }
  let cursor = res as Record<string, unknown>;

  const unwrapEnvelope = (obj: Record<string, unknown>): Record<string, unknown> => {
    const inner = obj.data;
    if (
      inner !== undefined &&
      inner !== null &&
      typeof inner === 'object' &&
      !Array.isArray(inner) &&
      (typeof obj.code === 'number' ||
        typeof obj.code === 'string' ||
        typeof obj.msg === 'string')
    ) {
      return inner as Record<string, unknown>;
    }
    return obj;
  };

  cursor = unwrapEnvelope(cursor);

  const nested = cursor.data;
  if (
    nested &&
    typeof nested === 'object' &&
    !Array.isArray(nested) &&
    Object.keys(cursor).length === 1 &&
    Object.prototype.hasOwnProperty.call(cursor, 'data')
  ) {
    return nested as Record<string, unknown>;
  }

  return cursor;
}

function normalizeDimMap(dimMap: unknown): Record<string, TAnomalyDimSeries> {
  if (!dimMap || typeof dimMap !== 'object' || Array.isArray(dimMap)) {
    return {};
  }
  const out: Record<string, TAnomalyDimSeries> = {};
  for (const [sk, raw] of Object.entries(dimMap as Record<string, unknown>)) {
    if (Array.isArray(raw)) {
      const arr = raw.filter(isAnomalyDimLeaf);
      if (arr.length) {
        out[sk] = arr;
      }
    } else if (isAnomalyDimLeaf(raw)) {
      out[sk] = raw;
    }
  }
  return out;
}

function normalizeMetricMapRoot(root: Record<string, unknown>): TAnomalyResultMap {
  const out: TAnomalyResultMap = {};
  for (const [metricKey, dimMap] of Object.entries(root)) {
    const normalized = normalizeDimMap(dimMap);
    if (Object.keys(normalized).length > 0) {
      out[metricKey] = normalized;
    }
  }
  return out;
}

/** 归一化后的异动载荷：顶层仅含 all / uaroi，可与旧版「扁平指标表」区分 */
export function isKpiOverviewAnomalyBundleShape(
  anomaly: unknown,
): anomaly is TKpiOverviewAnomalyBundle {
  if (!anomaly || typeof anomaly !== 'object' || Array.isArray(anomaly)) {
    return false;
  }
  const o = anomaly as Record<string, unknown>;
  const keys = Object.keys(o);
  if (keys.length === 0) {
    return false;
  }
  if (!keys.every(k => k === 'all' || k === 'uaroi')) {
    return false;
  }
  const { all, uaroi } = o;
  if (typeof all !== 'object' || all === null || Array.isArray(all)) {
    return false;
  }
  if (
    uaroi !== undefined &&
    uaroi !== null &&
    (typeof uaroi !== 'object' || Array.isArray(uaroi))
  ) {
    return false;
  }
  return true;
}

/**
 * 按 KPI 总览 Tab 口径选取 `all`（默认）或 `uaroi`（UA-ROI Campaign & UA 买量渠道）。
 * - bundle：见 {@link normalizeAnomalyApiPayload}
 * - 非 bundle：视为旧版扁平 `TAnomalyResultMap`，原样返回
 */
export function pickKpiOverviewAnomalyLayer(
  anomaly: unknown,
  scope?: TKpiOverviewDataScope,
): TAnomalyResultMap | undefined {
  if (!anomaly || typeof anomaly !== 'object' || Array.isArray(anomaly)) {
    return undefined;
  }
  if (isKpiOverviewAnomalyBundleShape(anomaly)) {
    if (scope === 'ua_roi_ua_media' && anomaly.uaroi) {
      return anomaly.uaroi;
    }
    return anomaly.all;
  }
  return anomaly as TAnomalyResultMap;
}

/**
 * 将 anomaly_result 各类响应规范为 {@link TKpiOverviewAnomalyBundle}（供下游与 mock 使用）
 */
export function normalizeAnomalyApiPayload(res: unknown): TKpiOverviewAnomalyBundle {
  const root = extractAnomalyResultRoot(res);
  if (!root) {
    return { all: {} };
  }
  // 与 pickKpiOverviewAnomalyLayer 一致：仅含 all /（可选）uaroi 即分层，勿要求两侧同时非空。
  // 否则 uaroi 为 null/缺失时会误判为扁平根，对整包做 normalizeMetricMapRoot 会掏空 all。
  if (isKpiOverviewAnomalyBundleShape(root)) {
    const { all: allLayer, uaroi: uaroiLayer } = root;
    const out: TKpiOverviewAnomalyBundle = {
      all: normalizeMetricMapRoot(allLayer as Record<string, unknown>),
    };
    if (
      uaroiLayer !== undefined &&
      uaroiLayer !== null &&
      typeof uaroiLayer === 'object' &&
      !Array.isArray(uaroiLayer)
    ) {
      out.uaroi = normalizeMetricMapRoot(uaroiLayer as Record<string, unknown>);
    }
    return out;
  }
  return { all: normalizeMetricMapRoot(root) };
}

const DEFAULT_API = '/api/v1/dashboard/pc/overview_daily/anomaly_result';

/**
 * 占位请求体标记：`disableAnomalyRequest` 为 true 时使用，避免 `beforeRequest` 返回 `false` 导致
 * PageContainer `Service` 提前 return 且不更新 loading（不在 Service 内改逻辑）。
 */
const ANOMALY_SERVICE_NO_REQUEST_KEY = '__anomalyServiceNoRequest';

/** 与 KpiEventMetricTrendService 内 kpiMetricsTrendBeforeRequest 日粒度最短区间一致（默认 14 天） */
const ANOMALY_TREND_MIN_RANGE_DAYS = 14;

/** 与 KpiAiSummaryService（pageConfig）一致：仅默认维度时拉取 overview_daily anomaly 接口 */
function isDefaultDimensionsForOverviewDaily(fv: Record<string, unknown>): boolean {
  const getList = (key: string, v: unknown): unknown[] | undefined => {
    if (Array.isArray(v)) {
      return v;
    }
    if (v && typeof v === 'object') {
      if (key === 'region') {
        return (v as { markets?: unknown[]; }).markets;
      }
      return (v as Record<string, unknown[]>)[key];
    }
    return undefined;
  };
  const isDef = (list: unknown[] | undefined): boolean =>
    !list ||
    list.length === 0 ||
    (list.length === 1 && (list[0] === 255 || list[0] === '255'));

  const dimKeys = ['system', 'campaign', 'ua_network', 'region'];
  return !dimKeys.some(k => !isDef(getList(k, fv[k])));
}

interface IAnomalyResolvedTimeRangeBody {
  start_time: string;
  end_time: string;
  period: string;
}

type TAnomalyResolvedTimeRange = IAnomalyResolvedTimeRangeBody | false;

/**
 * 与 kpiMetricsTrendBeforeRequest 时间链对齐（本文件内实现，不改动 KpiEventMetricTrendService）。
 */
function resolveAnomalyRequestTimeRange(
  filterVal: Record<string, unknown>,
  anomalMinTrendRangeDays?: number,
): TAnomalyResolvedTimeRange {
  const {
    rangeTime,
    timeType,
    diff,
    singleDaily,
    start_date: sd0,
    end_date: ed0,
    period: periodFromFilter,
  } = filterVal;

  let startTime = sd0 as string | undefined;
  let endTime = ed0 as string | undefined;
  if (singleDaily) {
    startTime = singleDaily as string;
    endTime = singleDaily as string;
  }
  if (Array.isArray(rangeTime) && rangeTime.length >= 2) {
    const [rt0, rt1] = rangeTime;
    startTime = rt0 as string;
    endTime = rt1 as string;
  }

  const timeTypeForFmt = (timeType as string) || 'Day';
  if (!singleDaily && startTime && endTime) {
    const rangeForCalc =
      Array.isArray(rangeTime) && rangeTime.length >= 2
        ? rangeTime
        : [startTime, endTime];
    const defaultTimeRange = getDefaultUtcDateValue(
      timeTypeForFmt,
      rangeForCalc,
    );
    const rangeTimeArr = diff
      ? defaultTimeRange
      : formatSingleTime(rangeForCalc, timeTypeForFmt);
    if (Array.isArray(rangeTimeArr) && rangeTimeArr.length >= 2) {
      const [nextStart, nextEnd] = rangeTimeArr;
      startTime = nextStart;
      endTime = nextEnd;
    }
  }

  let period: string | undefined = periodFromFilter as string | undefined;
  if (!period && timeType) {
    if (timeType === 'Month') {
      period = 'monthly';
    } else if (timeType === 'Week') {
      period = 'weekly';
    } else {
      period = 'daily';
    }
  }
  if (!period) {
    period = 'daily';
  }

  if (!startTime || !endTime) {
    return false;
  }

  const minDays =
    typeof anomalMinTrendRangeDays === 'number' && anomalMinTrendRangeDays > 0
      ? Math.floor(anomalMinTrendRangeDays)
      : ANOMALY_TREND_MIN_RANGE_DAYS;

  const rangeExpanded = expandKpiTrendRangeToMinDays(
    startTime,
    endTime,
    period,
    minDays,
  );

  return {
    start_time: rangeExpanded.start_time,
    end_time: rangeExpanded.end_time,
    period,
  };
}

/**
 * 接口约定：POST body 为 { game_code, start_time, end_time, period }。
 * 与 anomaly_ai_summary 一致：非默认维度时不请求。
 */
/** AnomalyService beforeRequest 的入参（由 Service 注入，含 filterVal、gameCode 等） */
interface IAnomalyReqData {
  filterVal?: Record<string, unknown>;
  gameCode?: string;
  anomalMinTrendRangeDays?: number;
  [key: string]: unknown;
}

function buildAnomalyRequestParams(reqData: IAnomalyReqData): Record<string, unknown> | false {
  const fv = reqData?.filterVal || {};
  const { gameCode } = reqData;
  if (!gameCode) {
    return false;
  }

  if (!isDefaultDimensionsForOverviewDaily(fv)) {
    return false;
  }

  const range = resolveAnomalyRequestTimeRange(
    fv,
    reqData?.anomalMinTrendRangeDays,
  );
  if (!range) {
    return false;
  }

  return {
    game_code: gameCode,
    start_time: range.start_time,
    end_time: range.end_time,
    period: range.period,
  };
}

/**
 * 将 Service 拉取到的 anomalyResultState 展开为 anomalyResult 传给下层（与 Prompt 示例结构一致）
 */
interface IAnomalyChildrenBridgeProps {
  children?: React.ReactNode;
  eventData?: unknown;
  extra?: Record<string, unknown>;
  /** 父级 Service 注入的 data（含 anomalyResultState、filter 等） */
  data?: {
    anomalyResultState?: {
      data?: TKpiOverviewAnomalyBundle;
      loading?: boolean;
      errorMsg?: string;
    };
    filter?: unknown;
    [key: string]: unknown;
  };
}

function AnomalyChildrenBridge(props: IAnomalyChildrenBridgeProps) {
  const { children, eventData, extra, data } = props;
  const st = data?.anomalyResultState;
  const payload = {
    ...extra,
    data,
    eventData,
    filterVal: data?.filter,
    anomalyResult: (st?.data ?? undefined) as TKpiOverviewAnomalyBundle | undefined,
    anomalyResultLoading: st?.loading,
    anomalyResultError: st?.errorMsg,
  };

  return (
    <>
      {React.Children.map(children, (child) => {
        // children 可能是文本/空节点，仅对有效 React 元素做 cloneElement
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child, payload as React.Attributes & Record<string, unknown>);
      })}
    </>
  );
}

/** AnomalyService 的 props（来自 pageConfig 编排层，字段较多且动态） */
interface IAnomalyServiceProps {
  style?: React.CSSProperties;
  className?: string;
  /** pageConfig 注入的配置对象 */
  config?: {
    disableAnomalyRequest?: boolean;
    api?: string | ((params: unknown, data?: unknown) => Promise<unknown>);
    beforeRequest?: string | ((d: IAnomalyReqData) => unknown);
    formatData?: (d: unknown) => unknown;
    anomalMinTrendRangeDays?: number;
  };
  children?: React.ReactNode;
  data?: Record<string, unknown>;
  eventData?: unknown;
  beforeRequest?: string | ((d: IAnomalyReqData) => unknown);
  formatData?: (d: unknown) => unknown;
  api?: string | ((params: unknown, data?: unknown) => Promise<unknown>);
  disableAnomalyRequest?: boolean;
  /** 其它透传 props 向子组件传递 */
  [key: string]: unknown;
}

/**
 * KPI Overview 异常说明 Service：请求 overview_daily/anomaly_result，向下传递 anomalyResult
 *
 * 配置 `disableAnomalyRequest: true`（props 或 config）时：不发真实 anomaly 请求，`anomalyResult` 为空，
 * 下游 `shouldApplyAnomalyMerge` 为 false，趋势上不展示异动；且通过占位 beforeRequest + 包装 api
 * 让 Service 正常结束 loading（无需改 Service.tsx）。
 */
export default function AnomalyService(props: IAnomalyServiceProps) {
  const {
    style,
    className,
    config,
    children,
    data: propsData,
    eventData,
    beforeRequest: beforeRequestProp,
    formatData: formatDataProp,
    api: apiProp,
    disableAnomalyRequest: disableAnomalyRequestProp,
    ...resetProps
  } = props || {};

  const disableAnomalyRequest = Boolean(
    disableAnomalyRequestProp ?? config?.disableAnomalyRequest,
  );

  const resolvedApi = apiProp ?? config?.api ?? DEFAULT_API;
  const resolvedBeforeRequest =
    beforeRequestProp ?? config?.beforeRequest ?? buildAnomalyRequestParams;

  const wrappedBeforeRequest = useCallback(
    (reqData: IAnomalyReqData) => {
      if (disableAnomalyRequest) {
        return { [ANOMALY_SERVICE_NO_REQUEST_KEY]: true };
      }
      if (typeof resolvedBeforeRequest === 'string') {
        return (stringToFunction(resolvedBeforeRequest) as (d: IAnomalyReqData) => unknown)(
          reqData,
        );
      }
      return (resolvedBeforeRequest as (d: IAnomalyReqData) => unknown)(reqData);
    },
    [disableAnomalyRequest, resolvedBeforeRequest],
  );

  const wrappedApi = useMemo(() => {
    if (!disableAnomalyRequest) {
      return resolvedApi;
    }
    return (params: Record<string, unknown>, data?: unknown) => {
      if (params?.[ANOMALY_SERVICE_NO_REQUEST_KEY] === true) {
        return Promise.resolve({});
      }
      if (typeof resolvedApi === 'function') {
        return (resolvedApi as (p: unknown, d?: unknown) => Promise<unknown>)(
          params,
          data,
        );
      }
      return dashboardRequest({
        url: String(resolvedApi),
        method: 'post',
        data: params,
      });
    };
  }, [disableAnomalyRequest, resolvedApi]);

  const defaultFormatData = (d: unknown) => normalizeAnomalyApiPayload(d);
  const formatData = formatDataProp ?? config?.formatData ?? defaultFormatData;
  

  const { gameCode } = useSelector(
    // 仅消费 dashboard.currentGame.game_code，局部结构类型
    (state: { dashboard?: { currentGame?: { game_code?: string; }; }; }) => ({
      gameCode: state?.dashboard?.currentGame?.game_code,
    }),
    shallowEqual,
  );

  const data = useMemo(
    () => ({
      gameCode,
      ...(typeof config?.anomalMinTrendRangeDays === 'number'
        ? { anomalMinTrendRangeDays: config.anomalMinTrendRangeDays }
        : {}),
      ...(propsData || {}),
    }),
    [gameCode, propsData, config?.anomalMinTrendRangeDays],
  );

  /**
   * 须列入 trackData，beforeRequest 才能收到 anomalMinTrendRangeDays（与 KpiEventMetricTrendService 的 trackData 机制一致）。
   * 未配置时值为 undefined，resolveAnomalyRequestTimeRange 回退默认 14 天。
   */
  const trackData = useMemo(
    () => ['gameCode', 'anomalMinTrendRangeDays'] as const,
    [],
  );

  return (
    <div className={className} style={style}>
      <Service
        // wrappedApi 是 string | ((params, data?) => Promise<unknown>)，与 Service 的 api 签名
        // string | ((data: any, outParame?: any) => void) 在运行时完全兼容；此处用断言对齐声明签名
        api={wrappedApi as string | ((data: unknown, outParame?: unknown) => void)}
        data={data}
        dataKey="anomalyResultState"
        trackData={[...trackData]}
        formatData={formatData}
        beforeRequest={wrappedBeforeRequest}
      >
        <AnomalyChildrenBridge eventData={eventData} extra={resetProps}>
          {children}
        </AnomalyChildrenBridge>
      </Service>
    </div>
  );
}
